import React, { useState, useCallback, useMemo } from "react";
import { Budget, BudgetType, BudgetLine } from "@akfdev-com/shared";
import DateRangePicker from "./DateRangePicker";
import BudgetTypeSelector from "./BudgetTypeSelector";
import BudgetLineEditor from "./BudgetLineEditor";
import { Button } from "../ui";

/** Local interface for line data used in the form */
interface BudgetLineInput {
  id: string;
  categoryId: string;
  categoryName: string;
  budgetAmount: number; // in pence
  notes?: string;
}

/** Form data structure */
export interface BudgetFormData {
  name: string;
  startDate: string;
  endDate: string;
  budgetType: BudgetType;
  notes: string;
  lines: BudgetLineInput[];
}

/** Validation errors for the form */
interface FormErrors {
  name?: string;
  startDate?: string;
  endDate?: string;
  dateRange?: string;
  budgetType?: string;
  lines?: string;
  submit?: string;
}

interface BudgetFormProps {
  /** Initial budget data (for edit mode) */
  initialBudget?: Budget;
  /** Initial budget lines (for edit mode) */
  initialLines?: BudgetLine[];
  /** Whether the form is in read-only mode */
  readOnly?: boolean;
  /** Callback when form is submitted */
  onSubmit: (data: BudgetFormData) => Promise<void>;
  /** Callback when form is cancelled */
  onCancel?: () => void;
  /** Whether the form is currently processing */
  isLoading?: boolean;
  /** Submit button text */
  submitButtonLabel?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Budget Form component.
 *
 * A comprehensive form for creating and editing budgets with:
 * - Date range picker (flexible start/end dates)
 * - Budget type selector (INCOME/EXPENSE/SAVINGS)
 * - Integrated budget line editor with category picker
 * - Full validation and error handling
 *
 * The form handles:
 * - Budget metadata (name, notes)
 * - Date range validation (startDate <= endDate)
 * - Budget type selection
 * - Budget line management (add/edit/remove)
 * - Currency conversion (pence <-> pounds)
 * - Form validation
 *
 * **Validates: Requirements 1.1, 3.1, 3.2, 3.3**
 */
const BudgetForm: React.FC<BudgetFormProps> = ({
  initialBudget,
  initialLines = [],
  readOnly = false,
  onSubmit,
  onCancel,
  isLoading = false,
  submitButtonLabel = "Create Budget",
  className = "",
}) => {
  // Form state
  const [formData, setFormData] = useState<BudgetFormData>(() => {
    if (initialBudget) {
      return {
        name: initialBudget.name,
        startDate: initialBudget.startDate,
        endDate: initialBudget.endDate,
        budgetType: initialBudget.budgetType,
        notes: initialBudget.notes || "",
        lines: initialLines.map((line) => ({
          id: line.id,
          categoryId: line.categoryId,
          categoryName: line.categoryName,
          budgetAmount: line.budgetAmount,
          notes: line.notes,
        })),
      };
    }

    return {
      name: "",
      startDate: "",
      endDate: "",
      budgetType: BudgetType.Expense,
      notes: "",
      lines: [],
    };
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);

  /**
   * Validate the form data
   */
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Budget name is required";
    }

    // Date validation
    if (!formData.startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (!formData.endDate) {
      newErrors.endDate = "End date is required";
    }

    // Date range validation
    if (formData.startDate && formData.endDate) {
      if (formData.startDate > formData.endDate) {
        newErrors.dateRange = "Start date must be before or equal to end date";
      }
    }

    // Budget type validation
    if (!formData.budgetType) {
      newErrors.budgetType = "Budget type is required";
    }

    // Lines validation (at least one line required)
    if (!formData.lines || formData.lines.length === 0) {
      newErrors.lines = "At least one budget line is required";
    }

    // Check if any lines are incomplete
    const incompleteLines = formData.lines.some(
      (line) => !line.categoryId || line.budgetAmount <= 0,
    );
    if (incompleteLines) {
      newErrors.lines =
        "All budget lines must have a category and amount greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitted(true);

      if (!validateForm()) {
        return;
      }

      try {
        await onSubmit(formData);
      } catch (err) {
        console.error("[BudgetForm] Submission error:", err);
        setErrors((prev) => ({
          ...prev,
          submit:
            err instanceof Error
              ? err.message
              : "Failed to save budget. Please try again.",
        }));
      }
    },
    [formData, validateForm, onSubmit],
  );

  /**
   * Handle name change
   */
  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, name: e.target.value }));
      if (submitted) {
        setErrors((prev) => ({ ...prev, name: undefined }));
      }
    },
    [submitted],
  );

  /**
   * Handle date range change
   */
  const handleDateRangeChange = useCallback(
    (startDate: string, endDate: string) => {
      setFormData((prev) => ({ ...prev, startDate, endDate }));
      if (submitted) {
        setErrors((prev) => ({
          ...prev,
          startDate: undefined,
          endDate: undefined,
          dateRange: undefined,
        }));
      }
    },
    [submitted],
  );

  /**
   * Handle budget type change
   */
  const handleBudgetTypeChange = useCallback(
    (budgetType: BudgetType) => {
      setFormData((prev) => ({ ...prev, budgetType }));
      if (submitted) {
        setErrors((prev) => ({ ...prev, budgetType: undefined }));
      }
    },
    [submitted],
  );

  /**
   * Handle budget lines change
   */
  const handleLinesChange = useCallback(
    (lines: BudgetLineInput[]) => {
      setFormData((prev) => ({ ...prev, lines }));
      if (submitted) {
        setErrors((prev) => ({ ...prev, lines: undefined }));
      }
    },
    [submitted],
  );

  /**
   * Handle notes change
   */
  const handleNotesChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, notes: e.target.value }));
    },
    [],
  );

  /**
   * Calculate total budgeted amount
   */
  const totalBudgeted = useMemo(() => {
    return formData.lines.reduce(
      (sum, line) => sum + (line.budgetAmount || 0),
      0,
    );
  }, [formData.lines]);

  /**
   * Format pence to display string (pounds)
   */
  const formatPence = (pence: number): string => {
    return (pence / 100).toFixed(2);
  };

  const isDisabled = readOnly || isLoading;

  return (
    <form onSubmit={handleSubmit} className={className} noValidate>
      {/* General error alert */}
      {errors.submit && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{errors.submit}</p>
        </div>
      )}

      {/* Budget Name */}
      <div className="mb-6">
        <label
          htmlFor="budget-name"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Budget Name
          <span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="text"
          id="budget-name"
          value={formData.name}
          onChange={handleNameChange}
          disabled={isDisabled}
          placeholder="e.g., 2024 Annual Budget"
          className={[
            "block w-full rounded-md shadow-sm",
            "focus:border-primary-500 focus:ring-primary-500 sm:text-sm",
            "disabled:bg-gray-100 disabled:cursor-not-allowed",
            errors.name ? "border-red-300" : "border-gray-300",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-describedby={errors.name ? "budget-name-error" : undefined}
        />
        {errors.name && (
          <p
            id="budget-name-error"
            className="mt-1 text-sm text-red-600"
            role="alert"
          >
            {errors.name}
          </p>
        )}
      </div>

      {/* Date Range */}
      <div className="mb-6">
        <DateRangePicker
          startDate={formData.startDate}
          endDate={formData.endDate}
          onChange={handleDateRangeChange}
          disabled={isDisabled}
          required
          errors={{
            startDate: errors.startDate,
            endDate: errors.endDate,
            dateRange: errors.dateRange,
          }}
        />
      </div>

      {/* Budget Type */}
      <div className="mb-6">
        <BudgetTypeSelector
          value={formData.budgetType}
          onChange={handleBudgetTypeChange}
          disabled={isDisabled}
          required
          error={errors.budgetType}
        />
      </div>

      {/* Budget Lines */}
      <div className="mb-6">
        <BudgetLineEditor
          lines={formData.lines}
          onChange={handleLinesChange}
          budgetType={formData.budgetType}
          disabled={isDisabled}
          readOnly={readOnly}
          error={errors.lines}
        />
      </div>

      {/* Total Display */}
      {formData.lines.length > 0 && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Total Budget
            </span>
            <span className="text-lg font-semibold text-gray-900">
              £{formatPence(totalBudgeted)}
            </span>
          </div>
        </div>
      )}

      {/* Notes */}
      <div className="mb-6">
        <label
          htmlFor="budget-notes"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Notes
        </label>
        <textarea
          id="budget-notes"
          value={formData.notes}
          onChange={handleNotesChange}
          disabled={isDisabled}
          rows={3}
          placeholder="Add any notes or context about this budget..."
          className={[
            "block w-full rounded-md shadow-sm",
            "focus:border-primary-500 focus:ring-primary-500 sm:text-sm",
            "disabled:bg-gray-100 disabled:cursor-not-allowed",
            "border-gray-300",
          ]
            .filter(Boolean)
            .join(" ")}
        />
      </div>

      {/* Form Actions */}
      <div className="flex gap-3">
        <Button type="submit" disabled={isDisabled} className="flex-1">
          {isLoading ? "Saving..." : submitButtonLabel}
        </Button>
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isDisabled}
            className="flex-1"
          >
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
};

export default BudgetForm;
